import { db, storage } from "../../firebase/firebaseConfig";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const PRODUCTS_COLLECTION = "products";
const CATEGORIES_COLLECTION = "categories";

// ── Image upload ────────────────────────────────────────────────
export async function uploadCatalogImage(file, pathPrefix) {
  if (!file) return null;
  const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
  const storageRef = ref(storage, `${pathPrefix}/${safeName}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

// ── Categories ───────────────────────────────────────────────────
export async function listCategories() {
  const snap = await getDocs(query(collection(db, CATEGORIES_COLLECTION), orderBy("order", "asc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createCategory({ name, image, order }) {
  return addDoc(collection(db, CATEGORIES_COLLECTION), {
    name,
    image: image || null,
    order: order ?? 0,
    createdAt: serverTimestamp(),
  });
}

export async function deleteCategory(categoryId) {
  return deleteDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
}

// ── Products ─────────────────────────────────────────────────────
export async function listProducts() {
  const snap = await getDocs(query(collection(db, PRODUCTS_COLLECTION), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createProduct(productData) {
  return addDoc(collection(db, PRODUCTS_COLLECTION), {
    ...productData,
    active: productData.active ?? true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateProduct(productId, productData) {
  return updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
    ...productData,
    updatedAt: serverTimestamp(),
  });
}

// Take a product off the storefront without destroying it.
//
// ProductsPage filters on `active !== false`, so this is all that's needed to
// make it disappear — and unlike deleteProduct it leaves the document in
// place, which matters because every past order references it by ID.
export async function setProductActive(productId, active) {
  return updateDoc(doc(db, PRODUCTS_COLLECTION, productId), {
    active: Boolean(active),
    updatedAt: serverTimestamp(),
  });
}

// Remove one color from a product without opening the editor. The variants
// array is rewritten wholesale because Firestore has no "remove element at
// index" operation on an array — arrayRemove matches by value, and a variant
// object with an image URL in it is not something you want to match by value.
export async function removeProductVariant(productId, colorKey) {
  const productRef = doc(db, PRODUCTS_COLLECTION, productId);
  const snap = await getDoc(productRef);
  if (!snap.exists()) throw new Error("That product no longer exists.");

  const variants = (snap.data().variants || []).filter(
    (v) => v.colorKey !== colorKey
  );

  return updateDoc(productRef, { variants, updatedAt: serverTimestamp() });
}

export async function deleteProduct(productId) {
  return deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
}

// ── One-time import of the existing static TAGGG products ──────────
// Converts the static file's shape (title/blurb) into the Firestore
// catalog's shape (name/description), preserving variants/sizes/priceIds
// as-is.
//
// Uses a fixed, predictable document ID per product (derived from its
// name) instead of a random auto-generated one. That's what actually
// makes this safe to click any number of times, including two clicks
// close together — a random ID has a race window where two clicks can
// both check "does this exist?" before either write lands, and both
// create a new doc. A fixed ID can't do that: writing to the same ID
// twice just overwrites the same document, it can never create a
// second one, no matter the timing.
//
// The same fixed ID is why a deleted import comes back if you press the
// button again. That is intentional — but it does mean "delete" is not how
// you retire an imported product. Hide it, or delete it and don't re-import.
function staticProductDocId(title) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return `static-${slug}`;
}

export async function importStaticProducts(staticProductsData) {
  const results = { imported: [], skipped: [] };

  for (const p of staticProductsData) {
    const docId = staticProductDocId(p.title);
    const ref = doc(db, PRODUCTS_COLLECTION, docId);
    const existingSnap = await getDoc(ref).catch(() => null);

    if (existingSnap?.exists()) {
      results.skipped.push(p.title);
      continue;
    }

    await setDoc(ref, {
      name: p.title,
      description: p.blurb || "",
      categoryId: null, // set from the admin panel after import
      hasSizes: true,
      hasColors: true,
      sizeSystem: "clothing",
      // The sizes this product actually comes in, not the whole clothing
      // system. The editor reads this list; if it's empty or wrong, every
      // save writes sizes the product doesn't stock.
      selectedSizes: p.variants?.[0]?.sizes?.map((s) => s.label) || [],
      image: p.variants?.[0]?.image || null,
      variants: (p.variants || []).map((v) => ({ ...v, active: v.active !== false })),
      importedFrom: "static-taggg",
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    results.imported.push(p.title);
  }

  return results;
}
